import React, { useState, useRef } from 'react';
import PropTypes from 'prop-types';
import { useLayout } from '../layout/context';
import useToggle from '../../utils/useToggle';
import windowSize from '../../utils/window-size';

// Components
import { Container } from '../styled/containers';
import Navigation from './navigation';
import { SkipToContent } from './navigation/styles';
import SearchBar from './search-bar';
import CountrySwitch from './country-switch';
import QuickExitButton from '../quick-exit-button';
import { MobileSearchToggle, MobileMenuToggle, DonateButton } from './buttons';
import { MobileButtonWrapper, DesktopButtonWrapper } from './buttons/styles';

// Styles
import {
  HeaderContainer,
  HeaderWrapper,
  HeaderRows,
  HeaderRowTop,
  HeaderRowBottom,
} from './styles';

import { Logo, LogoWrapper } from './logo';
import { sendEvent } from '../../utils/analytics';

const Header = ({
  logo,
  logoMobile,
  navigation,
  siblingSiteUrl,
  siblingSiteName,
  hideNavBar = false,
}) => {
  const [overlayMenuOpen, setOverlayMenuOpen] = useState(false);
  const [searchOpen, searchState] = useToggle(false);
  const { containsDonationHero, showQuickExitButton } = useLayout();
  const { isClient, isDesktop } = windowSize();

  const mobileMenuToggleClosedRef = useRef();
  const mobileMenuToggleOpenRef = useRef();

  return (
    <HeaderWrapper data-funnelbacknoindex>
      <Container $noMobilePadding>
        {showQuickExitButton ? (
          <QuickExitButton showQuickExitButton={showQuickExitButton} />
        ) : null}
        <SkipToContent href="#main">Skip to main content</SkipToContent>
        <HeaderContainer>
          {!searchOpen && (
            <LogoWrapper to="/" data-logoplacement="header">
              <Logo logo={logo} logoMobile={logoMobile} />
            </LogoWrapper>
          )}

          {!hideNavBar ? (
            <HeaderRows>
              <HeaderRowTop
                key={`hdrt-${isClient}`}
                data-testid="header-row-top"
              >
                {isDesktop ? (
                  <DesktopButtonWrapper>
                    <CountrySwitch
                      siblingSiteUrl={siblingSiteUrl}
                      siblingSiteName={siblingSiteName}
                    />
                    <SearchBar placement="desktopHeader" />
                  </DesktopButtonWrapper>
                ) : (
                  <MobileButtonWrapper>
                    {!searchOpen ? (
                      <>
                        {!containsDonationHero && <DonateButton size="small" />}
                        <MobileSearchToggle
                          active={searchOpen}
                          onClick={() => searchState()}
                        />
                        <MobileMenuToggle
                          active={overlayMenuOpen}
                          ref={mobileMenuToggleClosedRef}
                          onClick={() => {
                            setOverlayMenuOpen(true);
                            // Wait for mobile menu transformations to happen, then focus
                            requestAnimationFrame(() => {
                              mobileMenuToggleOpenRef?.current.focus();
                            });
                            sendEvent({
                              event: 'headerMenu',
                              menuAction: overlayMenuOpen ? 'Close' : 'Open',
                            });
                          }}
                        />
                      </>
                    ) : (
                      <>
                        <SearchBar
                          searchFocus={searchOpen}
                          searchOpen={searchOpen}
                          placement="mobileHeader"
                        />
                        <MobileSearchToggle
                          active={searchOpen}
                          onClick={() => searchState()}
                        />
                      </>
                    )}
                  </MobileButtonWrapper>
                )}
              </HeaderRowTop>

              <HeaderRowBottom key={`hdrb-${isClient}`}>
                <Navigation
                  pageData={navigation}
                  overlayMenuOpen={overlayMenuOpen}
                  setOverlayMenuOpen={setOverlayMenuOpen}
                  searchState={searchState}
                  siblingSiteName={siblingSiteName}
                  siblingSiteUrl={siblingSiteUrl}
                  isDesktop={isDesktop}
                  logo={logo}
                  logoMobile={logoMobile}
                  mobileMenuToggleOpenRef={mobileMenuToggleOpenRef}
                  mobileMenuToggleClosedRef={mobileMenuToggleClosedRef}
                />
                {!containsDonationHero && <DonateButton size="regular" />}
              </HeaderRowBottom>
            </HeaderRows>
          ) : null}
        </HeaderContainer>
      </Container>
    </HeaderWrapper>
  );
};

Header.propTypes = {
  logo: PropTypes.object.isRequired,
  logoMobile: PropTypes.object,
  navigation: PropTypes.object.isRequired,
  siblingSiteName: PropTypes.string,
  siblingSiteUrl: PropTypes.string,
  hideNavBar: PropTypes.bool,
};

export default Header;
